import Image from 'next/image';
import Slider from 'react-slick';
import {cn} from '@/lib/utils';
import {buttonVariants} from '../Button';
import {AnimatedLink} from '../AnimatedLink';
import {FiChevronLeft, FiChevronRight} from 'react-icons/fi';
import {IVillageDictionary} from '@/dictionaries/pages/village/types';
import {VillageSurrounding} from '@/services/types';

type Props = {
  lang: string;
  dict: IVillageDictionary;
  sliderRef: Slider | null;
  surrounding: VillageSurrounding;
  villaInPuglia: boolean;
};

export default function NearTheVillageSectionCarouselItem({
  lang,
  sliderRef,
  dict,
  surrounding,
  villaInPuglia,
}: Props) {
  const {discoverSicily, sicily, puglia, discover} = dict;
  const {image, translate, guideSurrounding} = surrounding.surrounding;
  const hasGuideSurrounding = guideSurrounding !== null;

  const surroundingTranslation =
    translate.find((translate) => translate.locale === lang) || translate[0];

  const {title, description} = surroundingTranslation;

  function transformTitleToSlug() {
    const {title} =
      translate.find((translation) => translation.title === 'en') ||
      translate[0];

    const normalizedTitle = title
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');

    const replacedSpecialChars = normalizedTitle
      .toLowerCase()
      .replace(/[^\w\s\']/gi, ' ')
      .replace("'", '');

    const finalSlug = replacedSpecialChars.trim().split(/\s+/).join('-');
    return finalSlug;
  }

  const getDiscoverLink = () => {
    const lowerCaseRegion = villaInPuglia
      ? puglia.toLowerCase()
      : sicily.toLocaleLowerCase();

    if (lang == 'en') {
      if (hasGuideSurrounding) {
        return `/${lowerCaseRegion}/${
          discoverSicily.slug
        }/${transformTitleToSlug()}`;
      }
      return `/${lowerCaseRegion}/${discoverSicily.slug}`;
    } else {
      if (hasGuideSurrounding) {
        return `/${lang}/${lowerCaseRegion}/${
          discoverSicily.slug
        }/${transformTitleToSlug()}`;
      }
      return `/${lang}/${lowerCaseRegion}/${discoverSicily.slug}`;
    }
  };

  return (
    <>
      <button
        className="hidden rounded-full z-10 text-zinc-400 desktop:flex justify-center
        group items-center bg-white shadow-lg overflow-hidden absolute translate-y-[-50%] mt-[-50px] w-[64px] h-[64px] top-[47.86%] left-[-4%]"
        type="button"
        id="desktop-nv-carrousel-previous"
        aria-label="Go to carousel previous item"
        onClick={sliderRef?.slickPrev}>
        <FiChevronLeft className="w-8 h-8 stroke-slate-600 opacity-30 group-hover:opacity-20 transition" />
      </button>
      <button
        id="desktop-nv-carrousel-next"
        aria-label="Go to carousel next item"
        className="hidden rounded-full z-10 text-zinc-400 desktop:flex justify-center
        group items-center bg-white shadow-lg overflow-hidden absolute translate-y-[-50%] mt-[-50px] w-[64px] h-[64px] top-[47.86%] right-[-4%]"
        type="button"
        onClick={sliderRef?.slickNext}>
        <FiChevronRight className="w-8 h-8 stroke-slate-600 opacity-30 group-hover:opacity-20 transition" />
      </button>
      <div className="slider-opacity-item-bg min-h-[500px] max-h-[850px] w-full shadow-default desktop:h-full desktop:ml-[20%] desktop:w-4/5 desktop:relative">
        <div className="absolute min-h-[500px] max-h-[850px] bottom-0 left-[50%] top-0 h-full w-full object-cover translate-x-[-50%] align-middle max-w-full desktop:left-0 desktop:translate-x-0 desktop:w-full">
          <Image
            src={`${process.env.NEXT_PUBLIC_S3_URI}/thumbs/1920w_webp/${image.path}${image.filename}`}
            alt={title}
            fill
            className="absolute"
            sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
            style={{
              objectFit: 'cover',
            }}
          />
        </div>
      </div>
      <section className="slider-opacity-item-content flex flex-col flex-wrap h-full w-full justify-center absolute top-0 left-0 p-[30px] text-center before:content-[''] before:bg-black before:opacity-40 before:absolute before:bottom-0 before:top-0 before:left-0 before:right-0 text-white desktop:w-[30em]  desktop:before:content-none desktop:bg-white desktop:shadow-default desktop:block desktop:h-auto desktop:py-[30px] desktop:px-[40px] desktop:text-left desktop:top-[10.71%] desktop:text-graphite transition-opacity duration-300 ease-in-out">
        <h3 className="text-xl font-semibold mb-[20px] z-10 desktop:text-4xl">
          {title}
        </h3>
        <div
          className="text-base max-h-[144px] line-clamp-6 z-10 desktop:max-h-none desktop:line-clamp-[unset]"
          dangerouslySetInnerHTML={{
            __html: description || '',
          }}
        />

        <a
          tabIndex={-1}
          className={cn(
            buttonVariants({variant: 'default', size: 'default'}),
            'z-10 py-[20px] px-[10px] mt-[30px] desktop:hidden',
          )}
          href={getDiscoverLink()}>
          {hasGuideSurrounding ? `${discover} ${title}` : discoverSicily.label}
        </a>
        {!villaInPuglia && (
          <AnimatedLink
            href={getDiscoverLink()}
            className="w-fit hidden desktop:block py-[20px] px-[10px] mt-[30px] desktop:px-[40px] desktop:w-fit">
            {hasGuideSurrounding ? `${discover} ${title}` : discoverSicily.label}
          </AnimatedLink>
        )}
      </section>
    </>
  );
}
